import React, { useState } from 'react';
import { DataGrid, NoRowsOverlay } from 'src/components/DataGrid';
import { GridColDef, GridRowModel } from '@mui/x-data-grid';
import { useTranslate } from '@im/react-localization';
import {
  EnduserAffiliationList,
  useEnduserListQuery,
} from '../../../hooks/deal-details';

import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import {
  TextField,
  InputAdornment,
  IconButton,
  SearchIcon,
} from 'src/views/ingramPrograms/components/SearchBarWithChips';
import { Pagination } from '@im/ui-lib/Pagination';
import { useDateTimeFormatter } from 'src/hooks/dateTimeFormatter';

interface EndUserProps {
  enduser: string;
  isAffiliated: boolean;
}
export default function EndUser(props: Readonly<EndUserProps>) {
  const translate = useTranslate();
  const endUserList = useEnduserListQuery(
    { bidId: props.enduser },
    !props.isAffiliated
  );
  const rows = endUserList.data ?? [];
  const PageSetup = { page: 1, pageSize: 10 };
  const [endUserID, setEndUserID] = useState<string>('');
  const [pageSetup, setPageSetup] = useState<{
    page: number;
    pageSize: number;
  }>(PageSetup);
  const formatDateTime = useDateTimeFormatter();
  const columnHeadersForEndUser = [
    {
      field: 'endUserName',
      headerName: translate('Common.Name'),
    },
    {
      field: 'endUserNumber',
      headerName: translate('VendorProgram.EnduserNumber'),
    },
    {
      field: 'cmpEnduserId',
      headerName: translate('VendorProgram.CmpEnduserIdNumber'),
    },
    {
      field: 'modifiedBy',
      headerName: translate('VendorProgram.ModifiedBy'),
    },
    {
      field: 'modifiedOn',
      headerName: translate('Common.ChangedDate'),
      renderCell: (props: any) =>
        typeof props.value === 'string'
          ? formatDateTime(props.value, true)
          : `--`,
    },
  ];
  const columns: GridColDef<EnduserAffiliationList>[] =
    columnHeadersForEndUser.map((column) => ({
      field: column.field,
      flex: 1,
      headerName: column.headerName,
      renderCell: column.renderCell ?? ((item) => item.value),
    }));

  const filteredRows = rows.filter(
    (row) =>
      row.endUserNumber?.startsWith(endUserID) ||
      row.cmpEnduserId?.startsWith(endUserID)
  );
  const paginatedRows = filteredRows.slice(
    (pageSetup.page - 1) * pageSetup.pageSize,
    pageSetup.page * pageSetup.pageSize
  );

  const requestListPagination = () => (
    <Pagination
      data-testid="gds-vp-ed-list-table-pagination"
      onPageChange={(page) => {
        setPageSetup({ ...pageSetup, page });
      }}
      onRowsPerPageChange={(pageSize) => {
        setPageSetup({ page: 1, pageSize });
      }}
      page={pageSetup.page}
      rowsPerPage={pageSetup.pageSize}
      totalCount={filteredRows.length}
    />
  );

  const handleOnChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const { value } = event.target;
    setPageSetup({ ...pageSetup, page: 1 });
    setEndUserID(value);
  };

  return (
    <>
      {/* <Stack direction="row" mt={2} spacing={1}>
                <FormControl sx={{ width: '275px' }}>
                  <DealSearchBar />
                </FormControl>
        </Stack> */}
      <Box p={2}>
        <Stack direction={'row'} justifyContent={'space-between'}>
          <TextField
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            data-testid="gds-vp-aff-end-user-search"
            name="endUserIDSearchField"
            onChange={handleOnChange}
            placeholder={translate('EndUser.EndUserIdPlaceholder')}
            sx={{ width: '30%' }}
            value={endUserID}
            variant="outlined"
          />
        </Stack>
      </Box>
      <DataGrid
        autoHeight
        columnBuffer={columns.length}
        columns={columns}
        components={{
          NoRowsOverlay: NoRowsOverlay,
          Pagination: requestListPagination,
        }}
        data-testid="gds-vp-aff-end-user-dialog-data-grid"
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableSelectionOnClick
        getRowHeight={() => 'auto'}
        getRowId={(row: GridRowModel) =>
          props.isAffiliated ? row.endUserAffiliationId : row.endUserNumber
        }
        loading={endUserList.isLoading}
        rows={paginatedRows as any}
        sx={{
          '.MuiDataGrid-columnHeader--moving': {
            backgroundColor: 'transparent',
          },
          '.MuiDataGrid-footerContainer>div': {
            flexWrap: 'wrap',
          },
          '.MuiDataGrid-footerContainer>div>div': {
            flex: 1,
          },
          '.MuiDataGrid-row': {
            minHeight: '52px !important',
          },
        }}
      />
    </>
  );
}
